/**
 * Máscaras de entrada.
 *
 * A máscara é aplicada enquanto a pessoa digita e o valor CRU é o que vai para
 * a API — a formatação é da tela, como toda representação (CONSTITUICAO.md §1
 * decide isso para dinheiro, e a mesma lógica vale para documento).
 */

/**
 * Formata CNPJ no padrão 00.000.000/0000-00 enquanto o usuário digita.
 *
 * Aceita letras nas 12 primeiras posições: desde julho de 2026 os CNPJ novos
 * são alfanuméricos, e só os 2 dígitos verificadores continuam numéricos.
 * Máscara que só aceita número recusaria qualquer empresa aberta a partir
 * dessa data.
 */
export function mascararCNPJ(bruto: string): string {
  const limpo = limparCNPJ(bruto)

  const base = limpo.slice(0, 12)
  const verificador = limpo.slice(12, 14).replace(/[^0-9]/g, '')

  let saida = base.slice(0, 2)
  if (base.length > 2) saida += '.' + base.slice(2, 5)
  if (base.length > 5) saida += '.' + base.slice(5, 8)
  if (base.length > 8) saida += '/' + base.slice(8, 12)
  if (verificador.length > 0) saida += '-' + verificador

  return saida
}

/** Remove a máscara e normaliza para maiúsculas — é o que vai para a API. */
export function limparCNPJ(bruto: string): string {
  return bruto.toUpperCase().replace(/[^0-9A-Z]/g, '').slice(0, 14)
}

/** Diz se o CNPJ está completo (14 caracteres). Não confere o verificador. */
export function cnpjCompleto(bruto: string): boolean {
  return limparCNPJ(bruto).length === 14
}

/**
 * Confere os dígitos verificadores pelo módulo 11.
 *
 * Cada caractere entra como (código ASCII − 48), o que faz a mesma conta valer
 * para o CNPJ numérico antigo e para o alfanumérico novo — o '0' é 48, então
 * dígito continua valendo o próprio número.
 *
 * Existe no front para o erro aparecer no passo em que a pessoa digitou, e não
 * no fim do wizard. Quem decide é o backend.
 */
export function cnpjValido(bruto: string): boolean {
  const cnpj = limparCNPJ(bruto)

  if (cnpj.length !== 14) return false
  if (!/^[0-9]{2}$/.test(cnpj.slice(12))) return false
  if (cnpj.split('').every((caractere) => caractere === cnpj[0])) return false

  const primeiro = digitoVerificador(cnpj.slice(0, 12))
  if (Number(cnpj[12]) !== primeiro) return false

  return Number(cnpj[13]) === digitoVerificador(cnpj.slice(0, 13))
}

const PESOS = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2]

function digitoVerificador(parcial: string): number {
  const pesos = PESOS.slice(PESOS.length - parcial.length)

  let soma = 0
  for (let i = 0; i < parcial.length; i++) {
    soma += (parcial.charCodeAt(i) - 48) * (pesos[i] ?? 0)
  }

  const resto = soma % 11
  return resto < 2 ? 0 : 11 - resto
}

/** Mantém só dígitos — para campos numéricos como quantidade de filiais. */
export function somenteDigitos(bruto: string): string {
  return bruto.replace(/[^0-9]/g, '')
}

/**
 * Formata CPF no padrão 000.000.000-00 enquanto o usuário digita.
 *
 * Ao contrário do CNPJ, o CPF NÃO tem formato alfanumérico previsto: a Receita
 * mudou a numeração do cadastro de empresa, não a de pessoa. Letra digitada
 * aqui é erro, e a máscara descarta em vez de levar adiante até o backend
 * recusar com uma mensagem que ninguém entende.
 */
export function mascararCPF(bruto: string): string {
  const limpo = limparCPF(bruto)

  let saida = limpo.slice(0, 3)
  if (limpo.length > 3) saida += '.' + limpo.slice(3, 6)
  if (limpo.length > 6) saida += '.' + limpo.slice(6, 9)
  if (limpo.length > 9) saida += '-' + limpo.slice(9, 11)

  return saida
}

/** Remove a máscara do CPF — é o que vai para a API. */
export function limparCPF(bruto: string): string {
  return somenteDigitos(bruto).slice(0, 11)
}

/** Diz se o CPF está completo (11 dígitos). Não confere o verificador. */
export function cpfCompleto(bruto: string): boolean {
  return limparCPF(bruto).length === 11
}

/**
 * Confere os dígitos verificadores do CPF pelo módulo 11.
 *
 * Existe no front para o erro aparecer no campo em que a pessoa digitou, e não
 * depois de submeter um formulário de vinte campos. Quem decide é o backend
 * (api/internal/dominio/documento/cpf.go) — este é o mesmo cálculo, e as duas
 * implementações têm teste próprio justamente porque são duas.
 */
export function cpfValido(bruto: string): boolean {
  const cpf = limparCPF(bruto)

  if (cpf.length !== 11) return false
  // Sequência repetida fecha a conta do módulo 11 e é o que alguém digita para
  // "pular" o campo.
  if (cpf.split('').every((caractere) => caractere === cpf[0])) return false

  if (Number(cpf[9]) !== digitoCPF(cpf.slice(0, 9))) return false
  return Number(cpf[10]) === digitoCPF(cpf.slice(0, 10))
}

/**
 * Um verificador de CPF.
 *
 * O peso decresce a partir de `tamanho + 1` — 10..2 nos 9 primeiros dígitos,
 * 11..2 nos 10 primeiros. Derivar do tamanho evita as duas tabelas de
 * constantes que divergem quando alguém mexe numa só.
 */
function digitoCPF(parcial: string): number {
  const peso = parcial.length + 1

  let soma = 0
  for (let i = 0; i < parcial.length; i++) {
    soma += Number(parcial[i]) * (peso - i)
  }

  const resto = soma % 11
  return resto < 2 ? 0 : 11 - resto
}

/**
 * Formata telefone brasileiro: (11) 3311-9080 no fixo, (11) 98844-2210 no
 * celular.
 *
 * A quebra depende do TOTAL de dígitos, não da posição: enquanto a pessoa
 * digita, 10 dígitos são fixo e 11 são celular. Fixar o hífen na quarta casa
 * faria o celular aparecer como (11) 9884-42210 durante toda a digitação.
 */
export function mascararTelefone(bruto: string): string {
  const limpo = limparTelefone(bruto)
  if (limpo.length === 0) return ''

  const ddd = limpo.slice(0, 2)
  if (limpo.length <= 2) return '(' + ddd

  const resto = limpo.slice(2)
  const corte = limpo.length > 10 ? 5 : 4

  if (resto.length <= corte) return '(' + ddd + ') ' + resto
  return '(' + ddd + ') ' + resto.slice(0, corte) + '-' + resto.slice(corte)
}

/** Remove a máscara do telefone — é o que vai para a API. */
export function limparTelefone(bruto: string): string {
  return somenteDigitos(bruto).slice(0, 11)
}

/**
 * Diz se o telefone tem DDD e tamanho brasileiro: 10 no fixo, 11 no celular.
 *
 * Nove dígitos é celular sem DDD — e telefone sem DDD não liga de outra
 * cidade, que é justamente quando a loja precisa ligar.
 */
export function telefoneCompleto(bruto: string): boolean {
  const limpo = limparTelefone(bruto)
  return limpo.length === 10 || limpo.length === 11
}

/** Formata CEP no padrão 00000-000. */
export function mascararCEP(bruto: string): string {
  const limpo = limparCEP(bruto)
  if (limpo.length <= 5) return limpo
  return limpo.slice(0, 5) + '-' + limpo.slice(5)
}

/** Remove a máscara do CEP — é o que vai para a API. */
export function limparCEP(bruto: string): string {
  return somenteDigitos(bruto).slice(0, 8)
}

/** Diz se o CEP está completo (8 dígitos). */
export function cepCompleto(bruto: string): boolean {
  return limparCEP(bruto).length === 8
}

/**
 * Formata o documento conforme o tipo do cliente.
 *
 * A API transporta o documento CRU e a tela decide a máscara — é a mesma
 * divisão de dinheiro (CONSTITUICAO.md §1) aplicada a documento: valor cru
 * atravessa, formatação é da borda. Sem o tipo não daria para escolher, porque
 * um CNPJ alfanumérico de 14 e um CPF de 11 não se distinguem pelo tamanho em
 * toda entrada parcial.
 */
export function mascararDocumento(tipo: 'pf' | 'pj', bruto: string): string {
  return tipo === 'pj' ? mascararCNPJ(bruto) : mascararCPF(bruto)
}

/** Formata dígitos digitados no padrão dd/mm/aaaa, conforme a pessoa digita. */
export function mascararDataBR(bruto: string): string {
  const d = somenteDigitos(bruto).slice(0, 8)
  if (d.length <= 2) return d
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`
}

/** `2026-09-04` → `04/09/2026`. String vazia devolve string vazia. */
export function dataISOparaBR(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(iso)) return ''
  const [ano, mes, dia] = iso.split('-')
  return `${dia}/${mes}/${ano}`
}

/** `04/09/2026` → `2026-09-04`, ou `null` se a data não existe de verdade. */
export function dataBRparaISO(br: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(br)
  if (!m) return null
  const [, diaTxt, mesTxt, anoTxt] = m
  const dia = Number(diaTxt)
  const mes = Number(mesTxt)
  const ano = Number(anoTxt)

  // `Date` aceita "31/02" como "3 de março" em vez de recusar — conferir dia,
  // mês e ano voltando da própria data é o que pega isso.
  const data = new Date(ano, mes - 1, dia)
  if (data.getFullYear() !== ano || data.getMonth() !== mes - 1 || data.getDate() !== dia) {
    return null
  }

  return `${anoTxt}-${mesTxt}-${diaTxt}`
}
